import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { getPriceSettings, addPriceValue, deletePriceValue } from '../../services/api';
import { formatDate, formatEur } from '../../lib/workStatus';
import { Trash2, Paintbrush, Car } from 'lucide-react';
import { toast } from 'sonner';

const today = () => new Date().toISOString().split('T')[0];
const ICONS = { paint_price: Paintbrush, transport_rate: Car };

const PriceCard = ({ price, onChanged }) => {
  const [form, setForm] = useState({ value: '', valid_from: today(), note: '' });
  const Icon = ICONS[price.key] || Paintbrush;
  const now = new Date();
  const currentId = price.history.find((h) => new Date(h.valid_from) <= now)?.id;

  const submit = async (e) => {
    e.preventDefault();
    if (form.value === '' || Number(form.value) < 0) { toast.error('Ievadiet cenu'); return; }
    try {
      onChanged(await addPriceValue(price.key, { value: Number(form.value), valid_from: `${form.valid_from}T00:00:00`, note: form.note || null }));
      toast.success('Cena saglabāta');
      setForm({ value: '', valid_from: today(), note: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās saglabāt');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Dzēst šo cenas ierakstu?')) return;
    try { onChanged(await deletePriceValue(price.key, id)); } catch (error) { toast.error(error.response?.data?.detail || 'Neizdevās dzēst'); }
  };

  return (
    <Card className="border-0 shadow-sm" data-testid={`price-card-${price.key}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2"><Icon className="w-4 h-4 text-[#1F3D2B]" /> {price.label}</span>
          <span className="text-[#1F3D2B]" data-testid={`price-current-${price.key}`}>{formatEur(price.value)} <span className="text-xs font-normal text-[#2E2E2E]/60">{price.unit}</span></span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-4 gap-2 items-end" data-testid={`price-form-${price.key}`}>
          <div className="space-y-1"><Label className="text-xs">Jaunā cena ({price.unit})</Label><Input type="number" step="0.01" min="0" value={form.value} onChange={(e) => setForm((p) => ({ ...p, value: e.target.value }))} data-testid={`price-value-${price.key}`} /></div>
          <div className="space-y-1"><Label className="text-xs">Spēkā no</Label><Input type="date" value={form.valid_from} onChange={(e) => setForm((p) => ({ ...p, valid_from: e.target.value }))} data-testid={`price-valid-from-${price.key}`} /></div>
          <div className="space-y-1"><Label className="text-xs">Piezīme</Label><Input value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} data-testid={`price-note-${price.key}`} /></div>
          <Button type="submit" variant="outline" data-testid={`price-submit-${price.key}`}>Saglabāt</Button>
        </form>
        <div className="divide-y text-sm" data-testid={`price-history-${price.key}`}>
          {price.history.map((h) => (
            <div key={h.id} className="flex items-center justify-between py-1.5">
              <span><span className="font-medium">{formatEur(h.value)}</span> <span className="text-[#2E2E2E]/60">no {formatDate(h.valid_from)}{h.note ? ` · ${h.note}` : ''}</span>{h.id === currentId && <Badge className="ml-2 bg-green-100 text-green-700">spēkā</Badge>}</span>
              {price.history.length > 1 && <Button size="sm" variant="ghost" onClick={() => remove(h.id)} data-testid={`price-delete-${h.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export const PricesTab = () => {
  const [prices, setPrices] = useState([]);

  useEffect(() => {
    getPriceSettings().then(setPrices).catch(() => toast.error('Neizdevās ielādēt cenas'));
  }, []);

  const replace = (updated) => setPrices((prev) => prev.map((p) => (p.key === updated.key ? updated : p)));

  return (
    <div className="space-y-4" data-testid="prices-tab">
      <p className="text-sm text-[#2E2E2E]/70">Krāsa un transports ir <strong>uzņēmuma izmaksas</strong> — tās tiek iekļautas objekta peļņas aprēķinā, bet neietekmē darbinieka algu. Transporta likme tiek piemērota objektiem, kuriem nav norādīta sava €/km likme.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {prices.map((p) => <PriceCard key={p.key} price={p} onChanged={replace} />)}
      </div>
    </div>
  );
};
